import { useMemo } from 'react'
import { Button, IconButton, InlineTitle } from '../../components/controls.js'
import {
  formatMinutes,
  useCardIds,
  useEstimateTotals,
  useRow,
  useRowIds,
  useSwimlane,
} from '../../state/selectors.js'
import { useBoardStore } from '../../state/board-store.js'
import { useUiStore } from '../../state/ui-store.js'
import { ArchitectureIcons } from '../context/ArchitectureIcons.js'
import { FindingsBadge } from '../findings/Findings.js'
import { HeadingNotesButton } from '../heading-notes/HeadingNotes.js'
import type { StoryMapActions } from './actions.js'
import { useMatchSet } from './filter-context.js'
import { VirtualCardList } from './VirtualCardList.js'
import styles from './StoryMap.module.css'

interface SwimlaneSectionProps {
  swimlaneId: string
  swimlaneIds: readonly string[]
  columnIds: readonly string[]
  actions: StoryMapActions
  dragDisabled: boolean
}

export function SwimlaneSection({
  swimlaneId,
  swimlaneIds,
  columnIds,
  actions,
  dragDisabled,
}: SwimlaneSectionProps) {
  const swimlane = useSwimlane(swimlaneId)
  const rowIds = useRowIds(swimlaneId)
  if (!swimlane) return null

  const index = swimlaneIds.indexOf(swimlaneId)

  return (
    <>
      <div className={styles.swimlane}>
        <div className={styles.swimlaneInner} data-heading>
          <div className={styles.swimlaneTitle}>
            <InlineTitle
              value={swimlane.title}
              label={`Rename swimlane ${swimlane.title}`}
              onCommit={(title) => actions.renameSwimlane(swimlaneId, title)}
            />
          </div>
          <HeadingNotesButton scope="swimlane" id={swimlaneId} title={swimlane.title} />
          <span className={styles.laneCount}>
            {rowIds.length} row{rowIds.length === 1 ? '' : 's'}
          </span>
          <IconButton
            aria-label={`Move swimlane ${swimlane.title} earlier`}
            disabled={index <= 0}
            onClick={() => actions.moveSwimlane(swimlaneId, 'earlier')}
          >
            &#9650;
          </IconButton>
          <IconButton
            aria-label={`Move swimlane ${swimlane.title} later`}
            disabled={index === swimlaneIds.length - 1}
            onClick={() => actions.moveSwimlane(swimlaneId, 'later')}
          >
            &#9660;
          </IconButton>
          <IconButton
            aria-label={`Architecture context for swimlane ${swimlane.title}`}
            title="Architecture context"
            onClick={() => useUiStore.getState().setContextOwner({ scope: 'swimlane', id: swimlaneId })}
          >
            <span aria-hidden="true">&#9671;</span>
          </IconButton>
          <Button onClick={() => actions.addRow(swimlaneId)}>Add row</Button>
          <IconButton
            danger
            aria-label={`Delete swimlane ${swimlane.title}`}
            onClick={() => actions.deleteSwimlane(swimlaneId)}
          >
            &#10005;
          </IconButton>
        </div>
      </div>

      {rowIds.length === 0 ? (
        <>
          <div className={styles.rowLabel}>
            <span className={styles.hiddenCount}>No rows yet</span>
            <Button onClick={() => actions.addRow(swimlaneId)}>Add row</Button>
          </div>
          {columnIds.map((columnId) => (
            <div key={columnId} className={styles.cell} />
          ))}
          <div className={styles.rowFiller} />
        </>
      ) : (
        rowIds.map((rowId, rowIndex) => (
          <RowStrip
            key={rowId}
            rowId={rowId}
            swimlaneIds={swimlaneIds}
            columnIds={columnIds}
            isFirst={rowIndex === 0}
            isLast={rowIndex === rowIds.length - 1}
            actions={actions}
            dragDisabled={dragDisabled}
          />
        ))
      )}
    </>
  )
}

interface RowStripProps {
  rowId: string
  swimlaneIds: readonly string[]
  columnIds: readonly string[]
  isFirst: boolean
  isLast: boolean
  actions: StoryMapActions
  dragDisabled: boolean
}

function RowStrip({ rowId, swimlaneIds, columnIds, isFirst, isLast, actions, dragDisabled }: RowStripProps) {
  const row = useRow(rowId)
  const swimlanes = useBoardStore((state) => state.entities?.swimlanes)
  const estimate = formatMinutes(useEstimateTotals().byRow[rowId] ?? null)
  if (!row) return null

  return (
    <>
      <div className={styles.rowLabel} data-heading>
        <div className={styles.titleWithIcons}>
          <ArchitectureIcons context={row.context} />
          <InlineTitle
            value={row.title}
            label={`Rename row ${row.title}`}
            onCommit={(title) => actions.renameRow(rowId, title)}
          />
          <HeadingNotesButton scope="row" id={rowId} title={row.title} />
          <FindingsBadge targetId={rowId} label={`row ${row.title}`} />
        </div>
        <div className={styles.controls}>
          {/* Only when something in the row is estimated: an absent total is not zero. */}
          {estimate ? (
            <span className={styles.estimate} title={`Estimated ${estimate} in this row`}>
              {estimate}
            </span>
          ) : null}
          <IconButton
            aria-label={`Prompt preview for row ${row.title}`}
            title="Prompt preview"
            onClick={() => useUiStore.getState().setPromptScope({ kind: 'row', id: rowId })}
          >
            <span aria-hidden="true">&#10095;</span>
          </IconButton>
          <IconButton
            aria-label={`Architecture context for row ${row.title}`}
            title="Architecture context"
            onClick={() => useUiStore.getState().setContextOwner({ scope: 'row', id: rowId })}
          >
            <span aria-hidden="true">&#9671;</span>
          </IconButton>
        </div>
        <div className={styles.controls}>
          <IconButton
            aria-label={`Move row ${row.title} earlier`}
            disabled={isFirst}
            onClick={() => actions.moveRow(rowId, 'earlier')}
          >
            &#9650;
          </IconButton>
          <IconButton
            aria-label={`Move row ${row.title} later`}
            disabled={isLast}
            onClick={() => actions.moveRow(rowId, 'later')}
          >
            &#9660;
          </IconButton>
          <IconButton danger aria-label={`Delete row ${row.title}`} onClick={() => actions.deleteRow(rowId)}>
            &#10005;
          </IconButton>
        </div>
        <select
          className={styles.rowSelect}
          aria-label={`Swimlane for row ${row.title}`}
          value={row.swimlaneId}
          onChange={(event) => actions.moveRowToSwimlane(rowId, event.target.value)}
        >
          {swimlaneIds.map((swimlaneId) => (
            <option key={swimlaneId} value={swimlaneId}>
              {swimlanes?.[swimlaneId]?.title ?? 'Swimlane'}
            </option>
          ))}
        </select>
      </div>

      {columnIds.map((columnId) => (
        <Cell
          key={columnId}
          rowId={rowId}
          columnId={columnId}
          actions={actions}
          dragDisabled={dragDisabled}
        />
      ))}
      <div className={styles.rowFiller} />
    </>
  )
}

interface CellProps {
  rowId: string
  columnId: string
  actions: StoryMapActions
  dragDisabled: boolean
}

function Cell({ rowId, columnId, actions, dragDisabled }: CellProps) {
  const allCardIds = useCardIds(rowId, columnId)
  const matchSet = useMatchSet()

  // With no filter this returns the store's own array, so the cell keeps its identity.
  const cardIds = useMemo(
    () => (matchSet ? allCardIds.filter((id) => matchSet.has(id)) : allCardIds),
    [allCardIds, matchSet],
  )
  const hidden = allCardIds.length - cardIds.length

  return (
    <div className={styles.cell} data-testid="cell" data-cell={`${rowId}|${columnId}`}>
      <VirtualCardList
        rowId={rowId}
        columnId={columnId}
        cardIds={cardIds}
        dragDisabled={dragDisabled}
        onOpen={actions.openCard}
        onMove={actions.moveCard}
        onPrompt={actions.promptCard}
      />
      {hidden > 0 ? (
        <span className={styles.hiddenCount}>
          {hidden} hidden by filters
        </span>
      ) : null}
      <div className={styles.addRow}>
        <button
          type="button"
          className={styles.addCard}
          onClick={() => actions.addCard(rowId, columnId)}
        >
          + Add card
        </button>
      </div>
    </div>
  )
}
